import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";

function fmt(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

// Older interview records are not guaranteed to store every audit metadata
// field as a primitive string. React throws when an object/array is rendered
// directly as a child, which previously blanked the entire Question audits tab
// when one legacy row contained structured metadata. Always convert audit
// metadata to display-safe text at the UI boundary.
function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function displaySeniority(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const level = value.level || value.seniority || value.seniority_level;
    if (level !== null && level !== undefined && level !== "") {
      const text = String(level);
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
  }
  const text = displayValue(value);
  return text === "—" ? text : text.charAt(0).toUpperCase() + text.slice(1);
}

const ACTION_COLORS = {
  "user.suspended": "bg-rose-50 text-rose-700 border-rose-200",
  "user.active": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "user.beta_granted": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "user.beta_revoked": "bg-amber-50 text-amber-800 border-amber-200",
  "user.credits_adjusted": "bg-sky-50 text-sky-700 border-sky-200",
  "user.sessions_revoked": "bg-slate-100 text-slate-700 border-slate-200",
  "user.deleted": "bg-black text-white border-black",
  "beta_code.created": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "beta_code.disabled": "bg-rose-50 text-rose-700 border-rose-200",
  "beta_code.reenabled": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "beta_code.expiry_updated": "bg-sky-50 text-sky-700 border-sky-200",
};

export default function AdminAudit() {
  const [tab, setTab] = useState("actions");
  const [actions, setActions] = useState([]);
  const [deletions, setDeletions] = useState([]);
  const [questionAudits, setQuestionAudits] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [a, d, q] = await Promise.all([
        api.get("/admin/audit", { params: { limit: 500 } }),
        api.get("/admin/deletion-audit", { params: { limit: 500 } }),
        api.get("/admin/question-audits", { params: { limit: 200 } }),
      ]);
      setActions(a.data.entries);
      setDeletions(d.data.entries);
      setQuestionAudits(q.data.audits || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-audit">
      <AdminNav title="Admin Audit Log" />

      <main className="max-w-7xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-1 flex-wrap" role="tablist">
            <TabBtn
              active={tab === "actions"}
              onClick={() => setTab("actions")}
              testId="admin-audit-tab-actions"
            >
              Actions ({actions.length})
            </TabBtn>
            <TabBtn
              active={tab === "deletions"}
              onClick={() => setTab("deletions")}
              testId="admin-audit-tab-deletions"
            >
              Deletions ({deletions.length})
            </TabBtn>
            <TabBtn
              active={tab === "questions"}
              onClick={() => setTab("questions")}
              testId="admin-audit-tab-questions"
            >
              Question audits ({questionAudits.length})
            </TabBtn>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs hover:bg-[color:var(--bg-surface-hover)]"
            data-testid="admin-audit-refresh"
          >
            Refresh
          </button>
        </div>

        <div className="rounded-2xl border border-[color:var(--border-stone)] bg-white overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]">Loading…</div>
          ) : tab === "actions" ? (
            <ActionsTable rows={actions} />
          ) : tab === "deletions" ? (
            <DeletionsTable rows={deletions} />
          ) : (
            <QuestionAuditsTable rows={questionAudits} />
          )}
        </div>

        <p className="text-[11px] text-[color:var(--text-secondary)]">
          The actions and deletion logs are append-only. Question audits contain only
          generated interview-question metadata, question design fields, and whether
          clarification was used. Résumé text, job description source text, candidate
          identity, transcripts, and answers are not included in the question-audit download.
        </p>
      </main>
    </div>
  );
}

function TabBtn({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
        active
          ? "bg-[color:var(--brand)] text-white border-[color:var(--brand)]"
          : "border-[color:var(--border-stone)] hover:bg-[color:var(--bg-surface-hover)]"
      }`}
    >
      {children}
    </button>
  );
}

function ActionsTable({ rows }) {
  if (rows.length === 0) {
    return <div className="p-6 text-sm text-[color:var(--text-secondary)]">No entries yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="admin-audit-actions-table">
        <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Action</th>
            <th className="text-left px-3 py-2">Admin</th>
            <th className="text-left px-3 py-2">Target</th>
            <th className="text-left px-3 py-2">Reason</th>
            <th className="text-left px-3 py-2">Meta</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.entry_id} className="border-t border-[color:var(--border-stone)] align-top">
              <td className="px-3 py-2 whitespace-nowrap">{fmt(e.at)}</td>
              <td className="px-3 py-2">
                <span
                  className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                    ACTION_COLORS[e.action] || "bg-slate-100 border-slate-200 text-slate-700"
                  }`}
                >
                  {e.action}
                </span>
              </td>
              <td className="px-3 py-2 font-mono break-all">{e.admin_user_id}</td>
              <td className="px-3 py-2 font-mono break-all">
                <div className="text-[10px] text-[color:var(--text-secondary)]">{e.target_type}</div>
                {e.target_id}
              </td>
              <td className="px-3 py-2 break-all">{e.reason || "—"}</td>
              <td className="px-3 py-2 font-mono text-[10px] break-all">
                {e.meta && Object.keys(e.meta).length ? JSON.stringify(e.meta) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeletionsTable({ rows }) {
  if (rows.length === 0) {
    return <div className="p-6 text-sm text-[color:var(--text-secondary)]">No deletions yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="admin-audit-deletions-table">
        <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2">Requested</th>
            <th className="text-left px-3 py-2">Completed</th>
            <th className="text-left px-3 py-2">Method</th>
            <th className="text-left px-3 py-2">Admin</th>
            <th className="text-left px-3 py-2">Anon reference</th>
            <th className="text-left px-3 py-2">Categories</th>
            <th className="text-left px-3 py-2">Retention exception</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.entry_id} className="border-t border-[color:var(--border-stone)] align-top">
              <td className="px-3 py-2 whitespace-nowrap">{fmt(e.request_at)}</td>
              <td className="px-3 py-2 whitespace-nowrap">{fmt(e.completed_at)}</td>
              <td className="px-3 py-2">{e.method}</td>
              <td className="px-3 py-2 font-mono break-all">{e.admin_user_id}</td>
              <td className="px-3 py-2 font-mono break-all">{e.anon_ref}</td>
              <td className="px-3 py-2 text-[10px]">
                {(e.categories || []).map((c) => (
                  <span
                    key={c}
                    className="inline-block rounded-full border border-[color:var(--border-stone)] px-2 py-0.5 mr-1 mb-1 uppercase tracking-widest"
                  >
                    {c}
                  </span>
                ))}
              </td>
              <td className="px-3 py-2 break-all">{e.retention_exception || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuestionAuditsTable({ rows }) {
  if (rows.length === 0) {
    return (
      <div className="p-6 text-sm text-[color:var(--text-secondary)]">
        No generated question sets are available yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="admin-question-audits-table">
        <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2">Generated</th>
            <th className="text-left px-3 py-2">Role</th>
            <th className="text-left px-3 py-2">Download</th>
            <th className="text-left px-3 py-2">Format</th>
            <th className="text-left px-3 py-2">Seniority</th>
            <th className="text-left px-3 py-2">Languages</th>
            <th className="text-left px-3 py-2">Questions</th>
            <th className="text-left px-3 py-2">Clarifications used</th>
            <th className="text-left px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const sessionId = displayValue(row.session_id, `unknown-${index + 1}`);
            const href = `${api.defaults.baseURL}/admin/question-audit/${encodeURIComponent(sessionId)}.csv`;
            return (
              <tr key={`${sessionId}-${index}`} className="border-t border-[color:var(--border-stone)] align-top">
                <td className="px-3 py-2 whitespace-nowrap">{fmt(row.created_at)}</td>
                <td className="px-3 py-2 min-w-[14rem]">
                  <div className="font-medium">{displayValue(row.role_title, "Not recorded")}</div>
                  <div className="mt-0.5 text-[10px] text-[color:var(--text-secondary)] font-mono break-all">
                    {sessionId}
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <a
                    href={href}
                    className="inline-flex items-center rounded-full bg-[color:var(--brand)] px-3 py-1.5 text-white hover:bg-[color:var(--brand-hover)] whitespace-nowrap"
                    data-testid={`admin-question-audit-download-${sessionId}`}
                  >
                    Download CSV
                  </a>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{displayValue(row.practice_format)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{displaySeniority(row.session_seniority)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div>UI: {displayValue(row.interface_language)}</div>
                  <div className="text-[10px] text-[color:var(--text-secondary)]">
                    Interview: {displayValue(row.interview_language, "en")}
                  </div>
                </td>
                <td className="px-3 py-2">{displayValue(row.question_count)}</td>
                <td className="px-3 py-2">{displayValue(row.clarification_request_count, "0")}</td>
                <td className="px-3 py-2">
                  <div>{displayValue(row.status)}</div>
                  <div className="text-[10px] text-[color:var(--text-secondary)]">
                    {displayValue(row.validation_status)}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
